import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Phone, ArrowRight, CheckCircle, Star, Clock, Shield, Award,
  Droplets, Flame, Construction, ShowerHead, Thermometer, Wrench,
  Home, ChevronDown, ChevronUp, MapPin
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CTASection from '../components/common/CTASection';
import ProjectsShowcase from '../components/common/ProjectsShowcase';
import { LocalBusinessSchema, FAQSchema, AIOptimizedSchema, GoogleMapsSchema } from '../components/seo/SchemaMarkup';
import Seo from '../components/seo/Seo';
import { businessInfo, services, testimonials, stats, faqs } from '../data/mock';
import { seoConfig } from '../data/seoConfig';

const iconMap = {
  Droplets: Droplets,
  Flame: Flame,
  Construction: Construction,
  ShowerHead: ShowerHead,
  Thermometer: Thermometer,
  Shovel: Wrench,
  Stethoscope: Shield,
  Home: Home
};

const HomePage = () => {
  const [openFaq, setOpenFaq] = useState(null);

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  return (
    <>
      <Seo {...seoConfig.home} />
      <LocalBusinessSchema />
      <FAQSchema customFaqs={faqs.slice(0, 4)} />
      <AIOptimizedSchema />
      <GoogleMapsSchema />
      
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="relative min-h-[90vh] flex items-center pt-32 pb-20 overflow-hidden">
          {/* Background */}
          <div className="absolute inset-0 bg-gradient-to-br from-[#1a1a1a] via-[#2d2d2d] to-[#1a1a1a]">
            <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%23ffffff%22%20fill-opacity%3D%220.03%22%3E%3Cpath%20d%3D%22M36%2034v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6%2034v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6%204V0H4v4H0v2h4v4h2V6h4V4H6z%22%2F%3E%3C%2Fg%3E%3C%2Fg%3E%3C%2Fsvg%3E')] opacity-50"></div>
            {/* Orange accent glow */}
            <div className="absolute top-0 right-0 w-96 h-96 bg-[#e85a24]/20 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#e85a24]/10 rounded-full blur-3xl"></div>
          </div>

          <div className="max-w-7xl mx-auto px-4 relative z-10">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Hero Content */}
              <div className="text-white">
                <div className="inline-flex items-center gap-2 bg-[#e85a24]/20 backdrop-blur-sm rounded-full px-4 py-2 mb-6 border border-[#e85a24]/30">
                  <Clock size={16} className="text-[#e85a24]" />
                  <span className="text-sm font-medium">24/7 Emergency Service Available</span>
                </div>
                
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
                  Your Trusted Plumber in{' '}
                  <span className="text-[#e85a24]">Odessa & Midland</span>
                </h1>
                
                <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                  From emergency repairs to planned installations, Basin Plumbing delivers 
                  reliable service throughout the Permian Basin. Licensed, insured, and 
                  available when you need us.
                </p>

                {/* Service Pills */}
                <div className="flex flex-wrap gap-3 mb-8">
                  {['Water Heaters', 'Drain Cleaning', 'Gas Lines', 'Trenchless'].map((service) => (
                    <span 
                      key={service}
                      className="bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-medium border border-white/10"
                    >
                      {service}
                    </span>
                  ))}
                </div>

                {/* CTA Buttons */}
                <div className="flex flex-col sm:flex-row gap-4">
                  <a href={`tel:${businessInfo.phone}`}>
                    <Button 
                      size="lg" 
                      className="bg-[#e85a24] hover:bg-[#d14a14] text-white px-8 py-6 text-lg w-full sm:w-auto group"
                    >
                      <Phone className="mr-2 group-hover:animate-pulse" size={20} />
                      {businessInfo.phone}
                    </Button>
                  </a>
                  <Link to="/contact">
                    <Button 
                      size="lg" 
                      variant="outline" 
                      className="border-white text-white hover:bg-white hover:text-[#1a1a1a] px-8 py-6 text-lg w-full sm:w-auto group"
                    >
                      Schedule Online
                      <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={18} />
                    </Button>
                  </Link>
                </div>

                {/* Google Reviews trust badge */}
                <a
                  href="https://www.google.com/maps/place/Basin+Plumbing/@31.8673,-102.4252,17z"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-3 mt-8 bg-white rounded-full pl-4 pr-5 py-2.5 shadow-lg hover:shadow-xl transition-shadow w-fit"
                  data-testid="google-reviews-badge"
                  aria-label="Basin Plumbing is rated 4.9 out of 5 from 247 Google reviews"
                >
                  <svg width="22" height="22" viewBox="0 0 48 48" aria-hidden="true">
                    <path fill="#4285F4" d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z"/>
                    <path fill="#34A853" d="M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7C7.96 41.07 15.4 46 24 46z"/>
                    <path fill="#FBBC05" d="M11.69 28.18C11.25 26.86 11 25.45 11 24s.25-2.86.69-4.18v-5.7H4.34A21.98 21.98 0 0 0 2 24c0 3.55.85 6.91 2.34 9.88l7.35-5.7z"/>
                    <path fill="#EA4335" d="M24 10.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 4.18 29.93 2 24 2 15.4 2 7.96 6.93 4.34 14.12l7.35 5.7c1.73-5.2 6.58-9.07 12.31-9.07z"/>
                  </svg>
                  <div className="flex flex-col leading-tight">
                    <div className="flex items-center gap-1.5">
                      <span className="text-[#1a1a1a] font-bold text-sm">4.9</span>
                      <span className="flex">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} size={14} className="text-yellow-400" fill="currentColor" />
                        ))}
                      </span>
                    </div>
                    <span className="text-gray-500 text-xs">247 Google reviews</span>
                  </div>
                </a>
              </div>

              {/* Hero Image/Visual */}
              <div className="hidden lg:block relative">
                <div className="relative">
                  <div className="absolute -inset-4 bg-gradient-to-r from-[#e85a24]/20 to-[#e85a24]/10 rounded-3xl blur-3xl"></div>
                  <img 
                    src="https://images.pexels.com/photos/6419128/pexels-photo-6419128.jpeg?auto=compress&cs=tinysrgb&w=600&h=500" 
                    alt="Professional plumber at work in Odessa TX"
                    className="relative rounded-2xl shadow-2xl w-full object-cover"
                  />
                  
                  {/* Floating Badge */}
                  <div className="absolute -bottom-6 -left-6 bg-white rounded-xl p-4 shadow-xl">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-[#e85a24] rounded-full flex items-center justify-center">
                        <Star className="text-white" fill="white" size={24} />
                      </div>
                      <div>
                        <p className="font-bold text-[#1a1a1a]">4.9/5 Rating</p>
                        <p className="text-sm text-gray-500">200+ Reviews</p>
                      </div>
                    </div>
                  </div>

                  {/* Experience Badge */}
                  <div className="absolute -top-4 -right-4 bg-[#e85a24] rounded-xl p-4 shadow-xl text-white">
                    <p className="text-3xl font-bold">10+</p>
                    <p className="text-sm text-white/80">Years Experience</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Scroll Indicator */}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
            <div className="w-8 h-12 border-2 border-white/30 rounded-full flex items-start justify-center pt-2">
              <div className="w-2 h-2 bg-[#e85a24] rounded-full animate-pulse"></div>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-12 bg-white border-b">
          <div className="max-w-7xl mx-auto px-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {stats.map((stat, index) => (
                <div key={index} className="text-center">
                  <p className="text-4xl md:text-5xl font-bold text-[#e85a24] mb-2">{stat.value}</p>
                  <p className="text-gray-600">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-2 bg-[#e85a24]/10 rounded-full px-4 py-2 mb-4">
                <Wrench size={16} className="text-[#e85a24]" />
                <span className="text-sm font-medium text-[#e85a24]">Our Services</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-[#1a1a1a] mb-4">
                Plumbing Services You Can Count On
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                From minor repairs to major installations, our licensed plumbers handle 
                every job with expertise and care.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {services.slice(0, 8).map((service) => {
                const IconComponent = iconMap[service.icon] || Wrench;
                return (
                  <Link key={service.id} to={`/services/${service.slug}`}>
                    <Card className="h-full hover:shadow-xl transition-all duration-300 hover:-translate-y-1 group border-0 bg-white">
                      <CardContent className="p-6">
                        <div className="w-14 h-14 bg-gray-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-[#e85a24] transition-colors">
                          <IconComponent className="text-[#1a1a1a] group-hover:text-white transition-colors" size={28} />
                        </div>
                        <h3 className="text-lg font-semibold text-[#1a1a1a] mb-2 group-hover:text-[#e85a24] transition-colors">
                          {service.title}
                        </h3>
                        <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                          {service.shortDesc}
                        </p>
                        <span className="inline-flex items-center text-[#e85a24] font-medium text-sm group-hover:gap-2 transition-all">
                          Learn More
                          <ArrowRight size={16} className="ml-1 group-hover:translate-x-1 transition-transform" />
                        </span>
                      </CardContent>
                    </Card>
                  </Link>
                );
              })}
            </div>

            <div className="text-center mt-12">
              <Link to="/services">
                <Button size="lg" variant="outline" className="border-[#1a1a1a] text-[#1a1a1a] hover:bg-[#1a1a1a] hover:text-white">
                  View All Services
                  <ArrowRight className="ml-2" size={18} />
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Recent Projects (local-SEO proof + trust) */}
        <ProjectsShowcase />

        {/* Why Choose Us Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-flex items-center gap-2 bg-[#e85a24]/10 rounded-full px-4 py-2 mb-4">
                  <Award size={16} className="text-[#e85a24]" />
                  <span className="text-sm font-medium text-[#e85a24]">Why Choose Us</span>
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-[#1a1a1a] mb-6">
                  The Permian Basin's Most Trusted Plumber
                </h2>
                <p className="text-gray-600 mb-8 leading-relaxed">
                  Basin Plumbing has been serving Odessa, Midland, and surrounding communities 
                  for over 10 years. Our commitment to quality work, fair pricing, and 
                  genuine customer care has made us the go-to plumber for thousands of families.
                </p>

                <div className="space-y-4">
                  {[
                    { icon: Clock, title: '24/7 Emergency Service', desc: 'We answer when you call—nights, weekends, holidays' },
                    { icon: Shield, title: 'Licensed & Insured', desc: 'Fully licensed Texas plumbers with comprehensive coverage' },
                    { icon: Award, title: 'Quality Guaranteed', desc: 'We stand behind every job with our satisfaction guarantee' },
                    { icon: CheckCircle, title: 'Upfront Pricing', desc: 'Know exactly what you\'ll pay before we start work' }
                  ].map((item, index) => (
                    <div key={index} className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl hover:bg-[#e85a24]/5 transition-colors">
                      <div className="w-12 h-12 bg-[#e85a24] rounded-lg flex items-center justify-center flex-shrink-0">
                        <item.icon className="text-white" size={24} />
                      </div>
                      <div>
                        <h4 className="font-semibold text-[#1a1a1a] mb-1">{item.title}</h4>
                        <p className="text-gray-600 text-sm">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="relative">
                <img 
                  src="/images/plumber-kitchen-sink.jpg" 
                  alt="Basin Plumbing plumber repairing pipes under a kitchen sink in Odessa, TX"
                  className="rounded-2xl shadow-xl w-full object-cover"
                />
                <div className="absolute bottom-6 left-6 right-6 bg-white/95 backdrop-blur-sm rounded-xl p-6 shadow-lg">
                  <div className="flex items-center gap-4">
                    <img 
                      src="https://images.unsplash.com/photo-1560250097-0b93528c311a?w=80&h=80&fit=crop" 
                      alt="Satisfied customer"
                      className="w-16 h-16 rounded-full object-cover"
                    />
                    <div className="flex-1">
                      <div className="flex gap-1 mb-1">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} size={16} className="text-yellow-400" fill="currentColor" />
                        ))}
                      </div>
                      <p className="text-gray-600 text-sm italic">
                        "Best plumbers in Odessa! Fast, professional, and fair pricing."
                      </p>
                      <p className="text-sm font-medium text-[#1a1a1a] mt-1">- Robert M., Odessa</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4">
            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Plumber",
              "@id": "https://basinplumbing.com/#organization",
              "review": testimonials.slice(0, 3).map((t) => ({
                "@type": "Review",
                "author": { "@type": "Person", "name": t.name },
                "reviewRating": { "@type": "Rating", "ratingValue": String(t.rating), "bestRating": "5" },
                "reviewBody": t.text
              }))
            }) }} />
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-2 bg-[#e85a24]/10 rounded-full px-4 py-2 mb-4">
                <Star size={16} className="text-[#e85a24]" />
                <span className="text-sm font-medium text-[#e85a24]">Reviews</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-[#1a1a1a] mb-4">
                What Our Customers Say
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-6">
                Don't just take our word for it—hear from homeowners we've helped
              </p>
              <a
                href="https://www.google.com/maps/place/Basin+Plumbing/@31.8673,-102.4252,17z"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 bg-white rounded-full pl-4 pr-5 py-2.5 shadow-md hover:shadow-lg transition-shadow"
                data-testid="reviews-aggregate"
                aria-label="Basin Plumbing is rated 4.9 out of 5 from 247 Google reviews"
              >
                <svg width="24" height="24" viewBox="0 0 48 48" aria-hidden="true">
                  <path fill="#4285F4" d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z"/>
                  <path fill="#34A853" d="M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7C7.96 41.07 15.4 46 24 46z"/>
                  <path fill="#FBBC05" d="M11.69 28.18C11.25 26.86 11 25.45 11 24s.25-2.86.69-4.18v-5.7H4.34A21.98 21.98 0 0 0 2 24c0 3.55.85 6.91 2.34 9.88l7.35-5.7z"/>
                  <path fill="#EA4335" d="M24 10.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 4.18 29.93 2 24 2 15.4 2 7.96 6.93 4.34 14.12l7.35 5.7c1.73-5.2 6.58-9.07 12.31-9.07z"/>
                </svg>
                <span className="text-2xl font-bold text-[#1a1a1a]">4.9</span>
                <span className="flex">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={16} className="text-yellow-400" fill="currentColor" />
                  ))}
                </span>
                <span className="text-gray-500 text-sm">· 247 Google reviews</span>
              </a>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {testimonials.slice(0, 3).map((testimonial, index) => (
                <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex gap-1 mb-4">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <Star key={i} size={18} className="text-yellow-400" fill="currentColor" />
                      ))}
                    </div>
                    <p className="text-gray-600 mb-6 leading-relaxed italic">
                      "{testimonial.text}"
                    </p>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-semibold text-[#1a1a1a]">{testimonial.name}</p>
                        <p className="text-sm text-gray-500">{testimonial.location}</p>
                      </div>
                      <span className="text-xs bg-[#e85a24]/10 text-[#e85a24] px-3 py-1 rounded-full">
                        {testimonial.service}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-[#1a1a1a] mb-4">
                Frequently Asked Questions
              </h2>
              <p className="text-xl text-gray-600">
                Find answers to common plumbing questions
              </p>
            </div>

            <div className="space-y-4">
              {faqs.slice(0, 5).map((faq, index) => (
                <div 
                  key={index} 
                  className="border border-gray-200 rounded-xl overflow-hidden hover:border-[#e85a24]/30 transition-colors"
                >
                  <button
                    onClick={() => toggleFaq(index)}
                    className="w-full px-6 py-4 flex items-center justify-between text-left bg-white hover:bg-gray-50 transition-colors"
                  >
                    <span className="font-semibold text-[#1a1a1a] pr-4">{faq.question}</span>
                    {openFaq === index ? (
                      <ChevronUp className="text-[#e85a24] flex-shrink-0" size={20} />
                    ) : (
                      <ChevronDown className="text-gray-400 flex-shrink-0" size={20} />
                    )}
                  </button>
                  <div className={`overflow-hidden transition-all duration-300 ${
                    openFaq === index ? 'max-h-96' : 'max-h-0'
                  }`}>
                    <p className="px-6 pb-4 text-gray-600 leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <div className="text-center mt-8">
              <Link to="/faqs">
                <Button variant="outline" className="border-[#1a1a1a] text-[#1a1a1a] hover:bg-[#1a1a1a] hover:text-white">
                  View All FAQs
                  <ArrowRight className="ml-2" size={16} />
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Service Areas Section */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-[#1a1a1a] mb-4">
                Proudly Serving the Permian Basin
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                From Odessa to Midland and throughout West Texas, Basin Plumbing delivers 
                fast, reliable service to homes and businesses across the region.
              </p>
            </div>

            {/* Local Service Highlights */}
            <div className="grid md:grid-cols-2 gap-8 mb-12">
              <div className="bg-white rounded-xl p-6 shadow-lg border-l-4 border-[#e85a24]">
                <h3 className="text-xl font-bold text-[#1a1a1a] mb-3 flex items-center gap-2">
                  <MapPin size={20} className="text-[#e85a24]" />
                  Odessa, TX — Our Home Base
                </h3>
                <p className="text-gray-600 mb-4">
                  Located at 3730 Kermit Hwy, we serve all of Odessa including Downtown, 
                  University area, Sherwood, Music City Mall area, and East Odessa. 
                  <strong> Average response time: 20-30 minutes.</strong>
                </p>
                <div className="flex flex-wrap gap-2">
                  {['Downtown Odessa', 'UTPB Area', 'Sherwood', 'East Odessa', 'West Odessa'].map((area) => (
                    <span key={area} className="text-xs bg-gray-100 px-3 py-1 rounded-full text-gray-700">
                      {area}
                    </span>
                  ))}
                </div>
              </div>
              
              <div className="bg-white rounded-xl p-6 shadow-lg border-l-4 border-[#1a3a5c]">
                <h3 className="text-xl font-bold text-[#1a1a1a] mb-3 flex items-center gap-2">
                  <MapPin size={20} className="text-[#1a3a5c]" />
                  <Link to="/plumbers-in-midland" className="hover:text-[#e85a24] transition-colors">
                    Midland, TX — Full Service Coverage
                  </Link>
                </h3>
                <p className="text-gray-600 mb-4">
                  We serve all Midland neighborhoods including Downtown, Grassland Estates, 
                  Midland Park Mall area, Midkiff, and Greenwood.
                  <strong> Average response time: 30-45 minutes.</strong>
                </p>
                <div className="flex flex-wrap gap-2">
                  {['Downtown Midland', 'Grassland Estates', 'Midkiff', 'Greenwood', 'Fannin Terrace'].map((area) => (
                    <span key={area} className="text-xs bg-gray-100 px-3 py-1 rounded-full text-gray-700">
                      {area}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* All Service Areas */}
            <div className="text-center mb-6">
              <p className="text-sm text-gray-500 uppercase tracking-wider font-medium">Also Serving</p>
            </div>
            <div className="flex flex-wrap justify-center gap-3">
              {businessInfo.serviceAreas.filter(area => area !== 'Odessa' && area !== 'Midland').map((area) => (
                <span 
                  key={area} 
                  className="flex items-center gap-2 px-5 py-2 bg-white border border-gray-200 rounded-full text-gray-700 font-medium"
                >
                  <MapPin size={14} />
                  {area}, TX
                </span>
              ))}
            </div>

            {/* Local Trust Signals */}
            <div className="mt-12 bg-gradient-to-r from-[#e85a24]/10 to-[#1a3a5c]/10 rounded-2xl p-8">
              <div className="grid md:grid-cols-3 gap-6 text-center">
                <div>
                  <p className="text-3xl font-bold text-[#e85a24]">20-45 min</p>
                  <p className="text-gray-600">Average Emergency Response</p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-[#1a3a5c]">5,000+</p>
                  <p className="text-gray-600">Local Jobs Completed</p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-[#e85a24]">10+ Years</p>
                  <p className="text-gray-600">Serving West Texas</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <CTASection />
      </main>

      <Footer />
    </>
  );
};

export default HomePage;
